/**
 * Система вычисления различий и трёхстороннего слияния для CSElement
 */

import { ICSElement } from '../types/interfaces';
import {
  DiffChange,
  MergeConflict,
  ThreeWayMergeResult,
  DiffResult,
  DiffVisualizationOptions
} from '../types/worker-interfaces';
import { SerializableElement } from '../types/worker-interfaces';
// WorkerSerializer удален - используем встроенную сериализацию CSElement

/**
 * Алгоритмы для вычисления diff
 */
export enum DiffAlgorithm {
  MYERS = 'myers',
  PATIENCE = 'patience',
  HISTOGRAM = 'histogram',
  SIMPLE = 'simple'
}

/**
 * Стратегии разрешения конфликтов
 */
export enum MergeStrategy {
  AUTO = 'auto',
  MANUAL = 'manual',
  OURS = 'ours',
  THEIRS = 'theirs',
  UNION = 'union'
}

/**
 * Основной движок для вычисления различий
 */
export class DiffEngine {
  constructor() {
    // Используем встроенную сериализацию CSElement
  }

  /**
   * Сериализация элемента для diff операций
   */
  private serializeForDiff(element: ICSElement): SerializableElement {
    const serialized = (element as any).serialize({
      includeChildren: true,
      includeData: true,
      includeMetadata: true
    });

    // Конвертируем в формат SerializableElement
    return {
      id: serialized.id,
      name: serialized.name || '',
      data: serialized.data || {},
      children: this.convertChildrenToSerializable(serialized.children || []),
      metadata: serialized.metadata
    };
  }

  /**
   * Конвертация дочерних элементов в формат SerializableElement
   */
  private convertChildrenToSerializable(children: any[]): Record<string, SerializableElement> {
    const result: Record<string, SerializableElement> = {};
    
    for (const child of children) {
      result[child.name || child.id] = {
        id: child.id,
        name: child.name || '',
        data: child.data || {},
        children: this.convertChildrenToSerializable(child.children || []),
        metadata: child.metadata
      };
    }
    
    return result;
  }

  /**
   * Вычисление различий между двумя элементами
   */
  computeDiff(
    source: ICSElement,
    target: ICSElement,
    options: {
      algorithm?: DiffAlgorithm;
      ignoreWhitespace?: boolean;
      ignoreCase?: boolean;
      contextLines?: number;
      includeVisualization?: boolean;
    } = {}
  ): DiffResult {
    const {
      algorithm = DiffAlgorithm.MYERS,
      contextLines = 3,
      includeVisualization = false
    } = options;

    const sourceData = this.serializeForDiff(source);
    const targetData = this.serializeForDiff(target);

    const changes = this.computeChanges(sourceData, targetData, algorithm);
    const summary = this.computeSummary(changes);

    const result: DiffResult = {
      changes,
      summary
    };

    if (includeVisualization) {
      result.visualization = this.generateVisualization(changes, {
        layout: 'side-by-side',
        highlightChanges: true,
        showLineNumbers: true,
        contextLines,
        wordWrap: true,
        theme: 'light'
      });
    }

    return result;
  }

  /**
   * Трёхстороннее слияние элементов
   */
  threeWayMerge(
    base: ICSElement,
    source: ICSElement,
    target: ICSElement,
    options: {
      strategy?: MergeStrategy;
      conflictResolution?: 'prompt' | 'auto';
      autoResolveThreshold?: number;
    } = {}
  ): ThreeWayMergeResult {
    const {
      strategy = MergeStrategy.AUTO
    } = options;

    const startTime = Date.now();

    try {
      // Сериализуем все три версии
      const baseData = this.serializeForDiff(base);
      const sourceData = this.serializeForDiff(source);
      const targetData = this.serializeForDiff(target);

      // Вычисляем изменения от базы к каждой ветке
      const sourceChanges = this.computeChanges(baseData, sourceData, DiffAlgorithm.MYERS);
      const targetChanges = this.computeChanges(baseData, targetData, DiffAlgorithm.MYERS);

      // Находим конфликты
      const conflicts = this.findConflicts(sourceChanges, targetChanges, baseData);

      // Выполняем слияние
      const mergeResult = this.performMerge(
        baseData,
        sourceData,
        targetData,
        sourceChanges,
        targetChanges,
        conflicts,
        strategy
      );

      const autoResolved = conflicts.filter(c => c.autoResolvable).length;
      const manualRequired = conflicts.length - autoResolved;

      return {
        merged: mergeResult.merged,
        conflicts: conflicts,
        autoResolved,
        manualRequired,
        success: manualRequired === 0,
        metadata: {
          algorithm: DiffAlgorithm.MYERS,
          strategy,
          executionTime: Date.now() - startTime
        }
      };
    } catch (error) {
      return {
        merged: this.serializeForDiff(base),
        conflicts: [],
        autoResolved: 0,
        manualRequired: 1,
        success: false,
        metadata: {
          algorithm: DiffAlgorithm.MYERS,
          strategy,
          executionTime: Date.now() - startTime
        }
      };
    }
  }

  /**
   * Вычисление изменений между двумя сериализованными элементами
   */
  private computeChanges(
    source: SerializableElement,
    target: SerializableElement,
    algorithm: DiffAlgorithm
  ): DiffChange[] {
    const changes: DiffChange[] = [];

    // Сравниваем основные свойства
    if (source.name !== target.name) {
      changes.push({
        type: 'modify',
        path: ['name'],
        oldValue: source.name,
        newValue: target.name,
        metadata: {
          confidence: 1.0,
          algorithm
        }
      });
    }

    // Сравниваем данные
    const dataChanges = this.compareData(source.data, target.data, ['data'], algorithm);
    changes.push(...dataChanges);

    // Сравниваем дочерние элементы
    const childrenChanges = this.compareChildren(source.children, target.children, ['children'], algorithm);
    changes.push(...childrenChanges);

    return changes;
  }

  /**
   * Сравнение данных элементов
   */
  private compareData(
    sourceData: Record<string, any>,
    targetData: Record<string, any>,
    path: string[],
    algorithm: DiffAlgorithm
  ): DiffChange[] {
    const changes: DiffChange[] = [];
    const allKeys = new Set([...Object.keys(sourceData), ...Object.keys(targetData)]);

    for (const key of allKeys) {
      const currentPath = [...path, key];
      const sourceValue = sourceData[key];
      const targetValue = targetData[key];

      if (!(key in sourceData)) {
        // Добавление
        changes.push({
          type: 'add',
          path: currentPath,
          newValue: targetValue,
          metadata: {
            confidence: 1.0,
            algorithm
          }
        });
      } else if (!(key in targetData)) {
        // Удаление
        changes.push({
          type: 'remove',
          path: currentPath,
          oldValue: sourceValue,
          metadata: {
            confidence: 1.0,
            algorithm
          }
        });
      } else if (!this.deepEqual(sourceValue, targetValue)) {
        // Изменение
        changes.push({
          type: 'modify',
          path: currentPath,
          oldValue: sourceValue,
          newValue: targetValue,
          metadata: {
            confidence: this.calculateConfidence(sourceValue, targetValue),
            algorithm
          }
        });
      }
    }

    return changes;
  }

  /**
   * Сравнение дочерних элементов
   */
  private compareChildren(
    sourceChildren: Record<string, SerializableElement>,
    targetChildren: Record<string, SerializableElement>,
    path: string[],
    algorithm: DiffAlgorithm
  ): DiffChange[] {
    const changes: DiffChange[] = [];
    const allIds = new Set([...Object.keys(sourceChildren), ...Object.keys(targetChildren)]);

    for (const id of allIds) {
      const currentPath = [...path, id];
      const sourceChild = sourceChildren[id];
      const targetChild = targetChildren[id];

      if (!sourceChild) {
        // Добавление дочернего элемента
        changes.push({
          type: 'add',
          path: currentPath,
          newValue: targetChild,
          metadata: {
            confidence: 1.0,
            algorithm
          }
        });
      } else if (!targetChild) {
        // Удаление дочернего элемента
        changes.push({
          type: 'remove',
          path: currentPath,
          oldValue: sourceChild,
          metadata: {
            confidence: 1.0,
            algorithm
          }
        });
      } else {
        // Рекурсивное сравнение дочернего элемента
        const childChanges = this.computeChanges(sourceChild, targetChild, algorithm);
        childChanges.forEach(change => {
          change.path = [...currentPath, ...change.path];
        });
        changes.push(...childChanges);
      }
    }

    return changes;
  }

  /**
   * Поиск конфликтов между изменениями
   */
  private findConflicts(
    sourceChanges: DiffChange[],
    targetChanges: DiffChange[],
    baseData: SerializableElement
  ): MergeConflict[] {
    const conflicts: MergeConflict[] = [];
    
    // Группируем изменения по путям
    const sourceChangesByPath = this.groupChangesByPath(sourceChanges);
    const targetChangesByPath = this.groupChangesByPath(targetChanges);

    // Находим пересекающиеся пути
    const conflictingPaths = new Set<string>();
    for (const path of sourceChangesByPath.keys()) {
      if (targetChangesByPath.has(path)) {
        conflictingPaths.add(path);
      }
    }

    // Анализируем конфликты
    for (const pathKey of conflictingPaths) {
      const sourceChange = sourceChangesByPath.get(pathKey)!;
      const targetChange = targetChangesByPath.get(pathKey)!;
      const path = sourceChange.path;

      // Получаем базовое значение
      const baseValue = this.getValueByPath(baseData, path);

      const conflict: MergeConflict = {
        path,
        base: baseValue,
        source: sourceChange.newValue ?? sourceChange.oldValue,
        target: targetChange.newValue ?? targetChange.oldValue,
        type: this.determineConflictType(sourceChange, targetChange),
        severity: this.calculateConflictSeverity(sourceChange, targetChange),
        autoResolvable: this.isAutoResolvable(sourceChange, targetChange),
        suggestions: this.generateConflictSuggestions(sourceChange, targetChange, baseValue)
      };

      conflicts.push(conflict);
    }

    return conflicts;
  }

  /**
   * Выполнение слияния
   */
  private performMerge(
    baseData: SerializableElement,
    _sourceData: SerializableElement,
    _targetData: SerializableElement,
    sourceChanges: DiffChange[],
    targetChanges: DiffChange[],
    conflicts: MergeConflict[],
    strategy: MergeStrategy
  ): { merged: SerializableElement } {
    // Начинаем с копии базовой версии
    const merged = this.deepClone(baseData);

    // Применяем неконфликтующие изменения
    const conflictPaths = new Set(conflicts.map(c => c.path.join('.')));
    
    // Применяем изменения из source
    for (const change of sourceChanges) {
      const pathKey = change.path.join('.');
      if (!conflictPaths.has(pathKey)) {
        this.applyChange(merged, change);
      }
    }

    // Применяем изменения из target
    for (const change of targetChanges) {
      const pathKey = change.path.join('.');
      if (!conflictPaths.has(pathKey)) {
        this.applyChange(merged, change);
      }
    }

    // Разрешаем конфликты согласно стратегии
    for (const conflict of conflicts) {
      this.resolveConflict(merged, conflict, strategy);
    }

    return { merged };
  }

  /**
   * Группировка изменений по путям
   */
  private groupChangesByPath(changes: DiffChange[]): Map<string, DiffChange> {
    const grouped = new Map<string, DiffChange>();
    
    for (const change of changes) {
      const pathKey = change.path.join('.');
      grouped.set(pathKey, change);
    }

    return grouped;
  }

  /**
   * Получение значения по пути
   */
  private getValueByPath(obj: any, path: string[]): any {
    let current = obj;
    for (const segment of path) {
      if (current && typeof current === 'object' && segment in current) {
        current = current[segment];
      } else {
        return undefined;
      }
    }
    return current;
  }

  /**
   * Определение типа конфликта
   */
  private determineConflictType(
    sourceChange: DiffChange,
    targetChange: DiffChange
  ): 'content' | 'structure' | 'metadata' {
    if (sourceChange.path[0] === 'children' || targetChange.path[0] === 'children') {
      return 'structure';
    }
    if (sourceChange.path[0] === 'metadata' || targetChange.path[0] === 'metadata') {
      return 'metadata';
    }
    return 'content';
  }

  /**
   * Вычисление серьёзности конфликта
   */
  private calculateConflictSeverity(
    sourceChange: DiffChange,
    targetChange: DiffChange
  ): 'low' | 'medium' | 'high' {
    if (sourceChange.type === 'remove' || targetChange.type === 'remove') {
      return 'high';
    }
    if (sourceChange.type === 'add' && targetChange.type === 'add') {
      return 'medium';
    }
    return 'low';
  }

  /**
   * Проверка возможности автоматического разрешения
   */
  private isAutoResolvable(sourceChange: DiffChange, targetChange: DiffChange): boolean {
    // Простая логика - можно расширить
    if (sourceChange.type === 'add' && targetChange.type === 'add') {
      return this.deepEqual(sourceChange.newValue, targetChange.newValue);
    }
    return false;
  }

  /**
   * Генерация предложений для разрешения конфликта
   */
  private generateConflictSuggestions(
    sourceChange: DiffChange,
    targetChange: DiffChange,
    baseValue: any
  ): Array<{ strategy: string; description: string; result: any }> {
    const suggestions = [];

    suggestions.push({
      strategy: 'ours',
      description: 'Использовать версию из source',
      result: sourceChange.newValue ?? sourceChange.oldValue
    });

    suggestions.push({
      strategy: 'theirs',
      description: 'Использовать версию из target',
      result: targetChange.newValue ?? targetChange.oldValue
    });

    if (baseValue !== undefined) {
      suggestions.push({
        strategy: 'base',
        description: 'Вернуться к базовой версии',
        result: baseValue
      });
    }

    return suggestions;
  }

  /**
   * Применение изменения к объекту
   */
  private applyChange(obj: any, change: DiffChange): void {
    const path = [...change.path];
    const lastSegment = path.pop()!;
    let current = obj;

    // Навигация к родительскому объекту
    for (const segment of path) {
      if (!(segment in current)) {
        current[segment] = {};
      }
      current = current[segment];
    }

    // Применение изменения
    switch (change.type) {
      case 'add':
      case 'modify':
        current[lastSegment] = change.newValue;
        break;
      case 'remove':
        delete current[lastSegment];
        break;
    }
  }

  /**
   * Разрешение конфликта
   */
  private resolveConflict(
    merged: SerializableElement,
    conflict: MergeConflict,
    strategy: MergeStrategy
  ): void {
    let resolvedValue: any;

    switch (strategy) {
      case MergeStrategy.OURS:
        resolvedValue = conflict.source;
        break;
      case MergeStrategy.THEIRS:
        resolvedValue = conflict.target;
        break;
      case MergeStrategy.AUTO:
        if (conflict.autoResolvable) {
          resolvedValue = conflict.target; // Предпочитаем target при автоматическом разрешении
        } else {
          resolvedValue = conflict.source; // Fallback к source
        }
        break;
      case MergeStrategy.UNION:
        resolvedValue = this.unionMerge(conflict.source, conflict.target);
        break;
      default:
        resolvedValue = conflict.base;
    }

    // Применяем разрешение
    this.setValueByPath(merged, conflict.path, resolvedValue);
  }

  /**
   * Установка значения по пути
   */
  private setValueByPath(obj: any, path: string[], value: any): void {
    const pathCopy = [...path];
    const lastSegment = pathCopy.pop()!;
    let current = obj;

    for (const segment of pathCopy) {
      if (!(segment in current)) {
        current[segment] = {};
      }
      current = current[segment];
    }

    current[lastSegment] = value;
  }

  /**
   * Объединяющее слияние
   */
  private unionMerge(source: any, target: any): any {
    if (Array.isArray(source) && Array.isArray(target)) {
      return [...new Set([...source, ...target])];
    }
    if (typeof source === 'object' && typeof target === 'object' && source && target) {
      return { ...source, ...target };
    }
    return target; // Предпочитаем target
  }

  /**
   * Вычисление сводки изменений
   */
  private computeSummary(changes: DiffChange[]): {
    additions: number;
    deletions: number;
    modifications: number;
  } {
    const summary = {
      additions: 0,
      deletions: 0,
      modifications: 0
    };

    for (const change of changes) {
      switch (change.type) {
        case 'add':
          summary.additions++;
          break;
        case 'remove':
          summary.deletions++;
          break;
        case 'modify':
          summary.modifications++;
          break;
      }
    }

    return summary;
  }

  /**
   * Генерация визуализации изменений
   */
  private generateVisualization(
    changes: DiffChange[],
    _options: DiffVisualizationOptions
  ): string {
    // Простая SVG визуализация
    const svg = `
      <svg width="800" height="600" xmlns="http://www.w3.org/2000/svg">
        <style>
          .addition { fill: #28a745; }
          .deletion { fill: #dc3545; }
          .modification { fill: #ffc107; }
          .text { font-family: monospace; font-size: 12px; }
        </style>
        ${changes.map((change, index) => {
          const y = 20 + index * 25;
          const color = change.type === 'add' ? 'addition' : 
                       change.type === 'remove' ? 'deletion' : 'modification';
          return `
            <rect x="10" y="${y - 15}" width="15" height="15" class="${color}" />
            <text x="35" y="${y}" class="text">
              ${change.type}: ${change.path.join('.')}
            </text>
          `;
        }).join('')}
      </svg>
    `;

    return svg;
  }

  /**
   * Глубокое сравнение объектов
   */
  private deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a !== typeof b) return false;

    if (typeof a === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      
      if (keysA.length !== keysB.length) return false;
      
      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!this.deepEqual(a[key], b[key])) return false;
      }
      
      return true;
    }

    return false;
  }

  /**
   * Глубокое клонирование объекта
   */
  private deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime()) as any;
    if (obj instanceof Array) return obj.map(item => this.deepClone(item)) as any;
    
    const cloned = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = this.deepClone(obj[key]);
      }
    }
    return cloned;
  }

  /**
   * Вычисление уверенности в изменении
   */
  private calculateConfidence(oldValue: any, newValue: any): number {
    if (typeof oldValue !== typeof newValue) return 1.0;
    if (typeof oldValue === 'string') {
      const similarity = this.stringSimilarity(oldValue, newValue);
      return 1.0 - similarity;
    }
    return 1.0;
  }

  /**
   * Вычисление схожести строк
   */
  private stringSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Расстояние Левенштейна
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,     // deletion
          matrix[j - 1][i] + 1,     // insertion
          matrix[j - 1][i - 1] + indicator  // substitution
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  }
} 